/**
 * ViewportCullingContext (PIPH-72) — opt-in viewport culling for layer
 * components.
 *
 * When present, descendant layer components (`AtomLayer`, `BondLayer`) skip
 * rendering elements that lie entirely outside the provided viewport
 * rectangle. When absent, layers render everything unchanged — the legacy
 * behavior, backward-compatible.
 *
 * ## When to use
 *
 * For large workspaces where many atoms and bonds lie off-screen (zoomed
 * in, panned away, or just drawing-in-the-large use cases like PIPHBuild).
 * The culling check is `O(n_atoms + n_bonds)` per render and is essentially
 * free at typical workspace sizes — but the savings in React reconciliation
 * and DOM node creation for hundreds of off-screen atoms are substantial.
 *
 * ## Usage
 *
 * Feed the current viewBox from `useZoomPan` directly to the provider:
 *
 * ```tsx
 * function MyViewer() {
 *   const { viewBox } = useZoomPan({ ... });
 *   const bounds = boundsFromViewBox(viewBox);
 *
 *   return (
 *     <SVGGenerationProvider>
 *       <ViewportCullingProvider bounds={bounds}>
 *         <ComposableSVG>
 *           <BondLayer />
 *           <AtomLayer />
 *         </ComposableSVG>
 *       </ViewportCullingProvider>
 *     </SVGGenerationProvider>
 *   );
 * }
 * ```
 *
 * The provider is inert unless `bounds` is defined, so callers can safely
 * wrap their tree unconditionally and feed `undefined` / `null` when
 * culling isn't needed (e.g., before zoom/pan state has initialized).
 *
 * ## Why opt-in
 *
 * Culling requires knowing the viewport rectangle in the same coordinate
 * system as atom positions. Consumers without zoom/pan (or with a fixed
 * fit-to-content viewBox) have no way to define a meaningful viewport and
 * don't benefit from culling anyway — everything already fits on screen.
 * Making culling opt-in keeps those consumers' rendering paths unchanged
 * and avoids surprising "where did my atoms go?" bugs when someone forgets
 * to plumb the bounds correctly.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  DEFAULT_CULLING_MARGIN,
  type ViewportBounds,
} from '../rendering/viewportCulling';

export interface ViewportCullingContextValue {
  bounds: ViewportBounds;
  margin: number;
}

const ViewportCullingContext =
  createContext<ViewportCullingContextValue | null>(null);

export interface ViewportCullingProviderProps {
  /**
   * Viewport rectangle in SVG coordinates. Atoms and bonds outside this
   * rectangle (plus the margin) will be culled from layer rendering.
   *
   * Pass `undefined` or `null` to disable culling — the provider becomes
   * inert and descendants render as if no culling context were present.
   */
  bounds: ViewportBounds | null | undefined;
  /**
   * Margin to apply around the viewport rectangle in SVG units. Atoms
   * within `margin` of the viewport are still rendered, which covers atom
   * visual extent and bonds that cross the viewport edge.
   *
   * Defaults to `DEFAULT_CULLING_MARGIN` (6.5 Å = Alchemie standard bond
   * length). Workspaces with unusually long bonds should increase this.
   */
  margin?: number;
  children: ReactNode;
}

/**
 * Provides viewport culling information to descendant layer components.
 *
 * Layer components (`AtomLayer`, `BondLayer`) consume this via
 * `useViewportCulling()` and filter their data accordingly. No effect if
 * `bounds` is null/undefined — renders as if no culling context existed.
 */
export function ViewportCullingProvider({
  bounds,
  margin = DEFAULT_CULLING_MARGIN,
  children,
}: ViewportCullingProviderProps) {
  const value = useMemo<ViewportCullingContextValue | null>(
    () => (bounds ? { bounds, margin } : null),
    [bounds, margin],
  );

  return (
    <ViewportCullingContext.Provider value={value}>
      {children}
    </ViewportCullingContext.Provider>
  );
}

/**
 * Access viewport culling state from the nearest `ViewportCullingProvider`.
 *
 * Returns `null` if no provider is present OR if the provider was given
 * `null` / `undefined` bounds. Layer components should treat `null` as
 * "no culling — render everything".
 */
export function useViewportCulling(): ViewportCullingContextValue | null {
  return useContext(ViewportCullingContext);
}
